import { useEffect, useRef, useState } from "react";

import useForm from "../../../../../hooks/useForm";
import { useValidationErrorClearer } from "./hooks";

import {
	type FormErrors,
	type MeetingFormSchema,
	type meetingFormSchema,
	type recurringMeetingFormSchema,
} from "./meeting-form-schema";
import { type MeetingFormValuesWithOptionalDates, getDefaultFormValues } from "./meeting-form-utils";

type MeetingFormModalSchema = typeof meetingFormSchema | typeof recurringMeetingFormSchema;

type MeetingFormState = {
	formValues: MeetingFormValuesWithOptionalDates;
	validationErrors: FormErrors;
	updateForm: (values: Partial<MeetingFormSchema>) => void;
	setNewDefaultValues: (values: MeetingFormSchema) => void;
	/** Validates the staged values and surfaces any errors on the form. */
	validateForm: () => boolean;
	resetValidationErrors: () => void;
};

/** Form state, validation errors and their clearing, shared by the meeting and series modals. */
export const useMeetingFormState = (schema: MeetingFormModalSchema): MeetingFormState => {
	const [validationErrors, setValidationErrors] = useState<FormErrors>({});
	const { validate, updateForm, state, setNewDefaultValues } = useForm({
		schema,
		defaultValues: getDefaultFormValues(),
		disableWarning: true,
	});
	// The form allows clearing dates while editing
	const formValues = state as MeetingFormValuesWithOptionalDates;

	useValidationErrorClearer(formValues, validationErrors, setValidationErrors);

	const validateForm = (): boolean => {
		const result = validate();
		if (result.isValid) {
			return true;
		}
		setValidationErrors(
			Object.fromEntries(
				Object.entries(result.errors || {}).map(([field, error]) => [field, error.message])
			) as FormErrors
		);
		return false;
	};

	return {
		formValues,
		validationErrors,
		updateForm,
		setNewDefaultValues,
		validateForm,
		resetValidationErrors: (): void => setValidationErrors({}),
	};
};

/** Runs `onOpen` once each time the modal opens. */
export const useRunOnOpen = (isOpen: boolean, onOpen: () => void): void => {
	const onOpenRef = useRef(onOpen);
	onOpenRef.current = onOpen;
	const hasRunRef = useRef(false);

	useEffect(() => {
		if (isOpen && !hasRunRef.current) {
			onOpenRef.current();
			hasRunRef.current = true;
		} else if (!isOpen) {
			hasRunRef.current = false;
		}
	}, [isOpen]);
};

/** Retry re-sends the failed request; continue re-sends it without the integration that failed. */
export const errorModalActions = <TVariables>(
	variables: TVariables | undefined,
	resubmit: (variables: TVariables) => void,
	withoutIntegration: (variables: TVariables) => TVariables
): { onRetry: () => void; onContinue: () => void } => ({
	onRetry: (): void => {
		if (variables) {
			resubmit(variables);
		}
	},
	onContinue: (): void => {
		if (variables) {
			resubmit(withoutIntegration(variables));
		}
	},
});
